import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api, type CvStatus } from "../../api/client";

export function CvStep({ minWords }: { minWords: number }) {
  const queryClient = useQueryClient();
  const [unreadable, setUnreadable] = useState<CvStatus | null>(null);
  const [pasted, setPasted] = useState("");
  const done = () => queryClient.invalidateQueries({ queryKey: ["me"] });

  const upload = useMutation({
    mutationFn: api.uploadCv,
    onSuccess: (status) => (status.readable ? done() : setUnreadable(status)),
  });
  const paste = useMutation({ mutationFn: api.pasteCv, onSuccess: done });

  if (unreadable) {
    return (
      <section>
        <h1>We couldn't read text from this file</h1>
        <p>
          We found {unreadable.words} words (it may be a scanned image). Upload a text-based PDF or
          DOCX, or paste your CV text below. You can also continue anyway.
        </p>
        <label htmlFor="cv-paste">CV text</label>
        <textarea
          id="cv-paste"
          rows={10}
          value={pasted}
          onChange={(e) => setPasted(e.target.value)}
        />
        {paste.isError && <p role="alert">{paste.error.message}</p>}
        <div className="actions">
          <button
            type="button"
            disabled={!pasted.trim() || paste.isPending}
            onClick={() => paste.mutate(pasted)}
          >
            Save pasted text
          </button>
          <button type="button" className="secondary" onClick={() => setUnreadable(null)}>
            Upload another file
          </button>
          <button type="button" className="secondary" onClick={done}>
            Continue anyway
          </button>
        </div>
      </section>
    );
  }

  return (
    <section>
      <h1>Upload your CV</h1>
      <p>
        PDF or DOCX. We keep only the text (at least {minWords} words helps), not the file. Your AI
        agent will read your own copy of the CV to fill your profile.
      </p>
      <label htmlFor="cv-file">CV file</label>
      <input
        id="cv-file"
        type="file"
        accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        disabled={upload.isPending}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) upload.mutate(file);
        }}
      />
      {upload.isPending && <p>Reading your CV…</p>}
      {upload.isError && <p role="alert">{upload.error.message}</p>}
    </section>
  );
}
